/**
 * Phase 7-lite — Persistent install pill in the page header.
 *
 * Shows a tiny `[ ⤓ INSTALL ]` chip in the top-right of any page for users
 * who:
 *   • are NOT running as a standalone PWA
 *   • are on iOS / Android / desktop Chromium
 *
 * Acts as a permanent escape hatch in case the user dismissed the bottom
 * banner. Tapping opens the same walkthrough (iOS) or native install
 * (Android/desktop) as the bottom pill.
 *
 * Self-dismisses once the app is installed (display-mode: standalone).
 */
import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { detectPlatform, canPromptInstall, promptInstall, isPreviewInstallMode } from '../utils/installPwa';
import { PWAInstallWalkthrough } from './PWAInstallWalkthrough';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

export function PWAInstallHeaderPill({ inline = false }) {
  const { isModern } = useTheme();
  const { user } = useAuth();
  const modernCustomer = isModern && user?.role === 'customer';
  const [show, setShow] = useState(false);
  const [walkthrough, setWalkthrough] = useState(false);
  const [platform, setPlatform] = useState(null);
  // V2 dashboard removed Feb 2026; no route suppression needed anymore.
  const suppressedRoute = false;

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const p = detectPlatform();
    setPlatform(p);
    const preview = isPreviewInstallMode();
    if (!preview && p.isStandalone) return undefined;
    if (!p.isIOS && !p.isAndroid && !p.isDesktop) return undefined;
    setShow(true);
    const onInstalled = () => setShow(false);
    window.addEventListener('pwa-installed', onInstalled);
    return () => window.removeEventListener('pwa-installed', onInstalled);
  }, []);

  const handleClick = async () => {
    if (!platform) return;
    if (platform.isIOS) {
      setWalkthrough(true);
      return;
    }
    if (canPromptInstall()) {
      const choice = await promptInstall();
      if (choice?.outcome === 'accepted') setShow(false);
    } else {
      setWalkthrough(true);
    }
  };

  // Modern customer pages render the pill inline inside ModernNavbar instead.
  if (modernCustomer !== inline) return null;

  if (!show || suppressedRoute) {
    return <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />;
  }

  if (inline) {
    return (
      <>
        <button
          onClick={handleClick}
          className="flex items-center gap-1 px-2.5 py-1.5 mr-1 rounded-full text-[11px] font-semibold text-white transition-colors hover:opacity-90"
          style={{ background: 'var(--am-emerald-700)' }}
          data-testid="pwa-install-header-pill"
          aria-label="Install Ash & Merch app"
        >
          <Download className="w-3 h-3" />Install
        </button>
        <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        className="fixed top-3 right-3 z-[50] flex items-center gap-1 bg-[#0C0612]/80 backdrop-blur-sm border border-[#39FF14]/40 px-2.5 py-1.5 rounded-full shadow-[0_2px_12px_rgba(57,255,20,0.2)] hover:border-[#39FF14] hover:bg-[#1A0A2A] transition-all"
        data-testid="pwa-install-header-pill"
        aria-label="Install Ash & Merch app"
      >
        <Download className="w-3 h-3 text-[#39FF14]" />
        <span className="font-mono text-[#39FF14] text-[10px] font-bold tracking-wider">
          INSTALL
        </span>
      </button>
      <PWAInstallWalkthrough open={walkthrough} onClose={() => setWalkthrough(false)} />
    </>
  );
}

export default PWAInstallHeaderPill;
